/**
 * VirtualList - lightweight windowed list (doc 33, cross-cutting standard #3
 * and #8: "scale by construction", smooth at 5,000+ rows).
 *
 * Renders only the rows visible in the scroll viewport (plus a small overscan),
 * so a Findings / Asset Inventory / Lineage list with thousands of entries stays
 * smooth and never freezes the federated remote. Supports variable row heights
 * (a group header is taller than a finding row) via `itemHeight` as a function.
 *
 * Deliberately dependency-free - we do NOT add react-window / react-virtual to
 * the shared monorepo lockfile while a parallel chat is working the same tree.
 * It is ~one screen of code and covers exactly what Discovery needs.
 */
import { useMemo, useRef, useState, type ReactNode, type CSSProperties } from 'react';
import { cn } from '@/lib/cn';

interface VirtualListProps<T> {
  items: T[];
  /** Viewport height in px. The list scrolls inside this. */
  height: number;
  /** Row height in px - a constant, or a function of index for mixed rows. */
  itemHeight: number | ((index: number) => number);
  renderRow: (item: T, index: number) => ReactNode;
  getKey: (item: T, index: number) => string;
  /** Extra rows rendered above/below the viewport to avoid blank flashes. */
  overscan?: number;
  className?: string;
  style?: CSSProperties;
}

export function VirtualList<T>({
  items,
  height,
  itemHeight,
  renderRow,
  getKey,
  overscan = 8,
  className,
  style,
}: VirtualListProps<T>): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);

  // Cumulative offsets: offsets[i] = top of row i; offsets[n] = total height.
  const { offsets, total } = useMemo(() => {
    const off = new Array<number>(items.length + 1);
    off[0] = 0;
    for (let i = 0; i < items.length; i++) {
      const h = typeof itemHeight === 'function' ? itemHeight(i) : itemHeight;
      off[i + 1] = off[i] + h;
    }
    return { offsets: off, total: off[items.length] ?? 0 };
  }, [items, itemHeight]);

  // Binary search for the first row whose bottom is past the viewport top.
  const firstVisible = useMemo(() => {
    let lo = 0;
    let hi = items.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (offsets[mid + 1] <= scrollTop) lo = mid + 1;
      else hi = mid;
    }
    return Math.max(0, lo - overscan);
  }, [offsets, scrollTop, items.length, overscan]);

  const lastVisible = useMemo(() => {
    const viewportBottom = scrollTop + height;
    let i = firstVisible;
    while (i < items.length && offsets[i] < viewportBottom) i++;
    return Math.min(items.length, i + overscan);
  }, [offsets, scrollTop, height, firstVisible, items.length, overscan]);

  const visible: ReactNode[] = [];
  for (let i = firstVisible; i < lastVisible; i++) {
    const item = items[i];
    visible.push(
      <div
        key={getKey(item, i)}
        style={{ position: 'absolute', top: offsets[i], left: 0, right: 0, height: offsets[i + 1] - offsets[i] }}
      >
        {renderRow(item, i)}
      </div>,
    );
  }

  return (
    <div
      ref={scrollRef}
      onScroll={(e) => setScrollTop((e.target as HTMLDivElement).scrollTop)}
      className={cn('relative overflow-y-auto', className)}
      style={{ height, ...style }}
    >
      <div style={{ height: total, position: 'relative' }}>{visible}</div>
    </div>
  );
}
